import { useState } from 'react';
import { BoutonDocument } from '../../components/BoutonDocument.jsx';
import { ListeDuClient } from '../../components/ListeDuClient.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { etapeDeLaFacture, suiviAffaires } from '../../lib/suivi-affaires';
import { compteDansLeCa, ttcDocument } from '../../lib/vat';
import { C } from '../../theme/palette';

const cellule = { padding: '7px 4px', fontSize: 10, verticalAlign: 'middle' };
const sousTitre = { fontSize: 10.5, fontWeight: 900, color: C.navy, margin: '6px 0 2px' };
const entete = {
  ...cellule,
  fontSize: 8.5,
  fontWeight: 800,
  color: C.muted,
  textTransform: 'uppercase',
  textAlign: 'left',
};

/** L'état d'une facture, en mots simples et en couleur. */
function etat(f) {
  if (f.statut === 'Payée') {
    return [
      f.payeeLe || f.paidDate ? trf('✅ payée le {0}', f.payeeLe || f.paidDate) : tr('✅ payée'),
      C.green,
    ];
  }
  if (f.statut === 'Brouillon') return [tr('✏️ à envoyer'), C.purple];
  if (f.statut === 'Annulée') return [tr('annulée'), C.muted];
  if (f.retard > 0) return [trf('⏰ en retard ({0} j)', f.retard), C.red];
  return [tr('⏳ attend le paiement'), C.amber];
}

/** Une ligne du tableau : étape, numéro, montant, état, document. */
function Ligne({ etape, num, montant, texte, couleur, chemin, terminer }) {
  return (
    <tr style={{ borderTop: '1px solid ' + C.border }}>
      <td style={{ ...cellule, fontWeight: 800, color: C.ink }}>{etape}</td>
      <td style={{ ...cellule, color: C.grey }}>{num || '—'}</td>
      <td style={{ ...cellule, fontWeight: 800, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {fmt(montant)}
      </td>
      <td style={{ ...cellule, color: couleur || C.grey, fontWeight: 700 }}>{texte}</td>
      <td style={{ ...cellule, width: 34, textAlign: 'right' }}>
        {chemin ? (
          <BoutonDocument chemin={chemin} label="📄" color={C.navy} sm />
        ) : terminer ? (
          <button
            type="button"
            aria-label={tr('Terminer le brouillon')}
            onClick={terminer}
            style={{
              border: 'none',
              borderRadius: 7,
              background: C.purple,
              color: '#fff',
              fontSize: 11,
              padding: '4px 7px',
              cursor: 'pointer',
            }}
          >
            ✎
          </button>
        ) : null}
      </td>
    </tr>
  );
}

/** Le tableau d'un dossier : les lignes, puis ce qui reste à encaisser. */
function Tableau({ children, total, paye, reste }) {
  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={entete}>{tr('Étape')}</th>
            <th style={entete}>{tr('N°')}</th>
            <th style={{ ...entete, textAlign: 'right' }}>{DEVISE}</th>
            <th style={entete}>{tr('État')}</th>
            <th style={entete} />
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {total != null && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 6,
            background: C.navyXL,
            borderRadius: 9,
            padding: '8px 10px',
            marginTop: 6,
            fontSize: 10,
            fontWeight: 800,
          }}
        >
          <span>{trf('Total {0}', fmt(total))}</span>
          <span style={{ color: C.green }}>{trf('Encaissé {0}', fmt(paye))}</span>
          <span style={{ color: reste > 0.005 ? C.amber : C.green }}>
            {trf('Reste à encaisser {0}', fmt(Math.max(0, reste)))}
          </span>
        </div>
      )}
    </>
  );
}

/**
 * Un dossier repliable du classeur. Ce qui se charge du serveur (la liste du
 * client) ne se demande qu'à l'ouverture du dossier.
 */
function Dossier({ titre, sousTitre, droite, children, aLOuverture }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <details
      data-testid="dossier-factures"
      onToggle={(e) => setOuvert(e.currentTarget.open)}
      style={{ borderTop: '1px solid ' + C.border }}
    >
      <summary
        style={{
          listStyle: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 0',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 900, color: C.ink }}>{titre}</div>
          {sousTitre ? <div style={{ fontSize: 9, color: C.muted }}>{sousTitre}</div> : null}
        </div>
        <div style={{ fontSize: 9.5, fontWeight: 800, color: C.navy, textAlign: 'right' }}>
          {droite}
        </div>
        <div style={{ fontSize: 10, color: C.muted }}>▸</div>
      </summary>
      <div style={{ paddingBottom: 10 }}>
        {children}
        {ouvert && aLOuverture ? aLOuverture() : null}
      </div>
    </details>
  );
}

/**
 * Les factures des clients dans le classeur, une seule fois et simplement :
 * un dossier par client, et dedans un tableau par chantier — étape, numéro,
 * montant, état — de l'offre à la facture finale, avec ce qui reste à
 * encaisser, ses factures sans offre, puis tout ce qui est joint à ses offres
 * et factures : le matériel acheté, la sous-traitance, les photos.
 *
 * <p>Le patron les cherchait à deux endroits (la liste des factures et le
 * dossier du chantier) et trouvait l'ensemble trop compliqué : tout est ici.
 *
 * @param {object} props propriétés
 * @param {object[]} props.factures factures de l'exercice affiché
 * @param {object[]} [props.facturesToutes] toutes les factures, pour suivre une affaire sur plusieurs années
 * @param {object[]} [props.devis] offres
 * @param {object[]} [props.chantiers] chantiers
 * @param {object[]} [props.clients] clients
 * @param {(module: string) => void} [props.onNav] navigation : un brouillon se termine dans Factures
 * @returns {import('react').ReactElement} la tuile des factures clients
 */
export function FacturesClientsClasseur({
  factures,
  facturesToutes,
  devis,
  chantiers,
  clients,
  onNav,
}) {
  const toutes = facturesToutes || factures || [];
  const nomClient = (id) => (clients || []).find((c) => c.id === id)?.nom || '';
  const affaires = suiviAffaires({ devis, factures: toutes, chantiers }).affaires;
  const liees = new Set(
    affaires.flatMap((a) => [...a.acomptes, ...a.finales, ...a.enPreparation].map((f) => f.id)),
  );
  const autres = (factures || []).filter(
    (f) =>
      f.type !== 'avoir' && !liees.has(f.id) && (compteDansLeCa(f) || f.statut === 'Brouillon'),
  );
  const terminer = (f) => (f.statut === 'Brouillon' && onNav ? () => onNav('factures') : null);
  const chemin = (f) =>
    Number.isInteger(f.id) && f.statut !== 'Brouillon' ? `/factures/${f.id}/document` : null;
  const nb = liees.size + autres.length;
  // Un dossier par client : ses chantiers, ses factures sans offre, et tout ce
  // qui est joint à ses offres et factures (matériel, sous-traitance, photos).
  const parClient = new Map();
  const dossierDe = (clientId) => {
    if (!parClient.has(clientId)) parClient.set(clientId, { clientId, affaires: [], autres: [] });
    return parClient.get(clientId);
  };
  affaires.forEach((a) => dossierDe(a.clientId).affaires.push(a));
  autres.forEach((f) => dossierDe(f.clientId).autres.push(f));
  const dossiers = [...parClient.values()]
    .map((d) => {
      const idsFactures = new Set([
        ...d.affaires.flatMap((a) =>
          [...a.acomptes, ...a.finales, ...a.enPreparation].map((f) => f.id),
        ),
        ...d.autres.map((f) => f.id),
      ]);
      const payeesAutres = d.autres.filter((f) => f.statut === 'Payée');
      return {
        ...d,
        devis: (devis || []).filter((x) => d.affaires.some((a) => a.id === x.id)),
        factures: toutes.filter((f) => idsFactures.has(f.id)),
        nbFactures: idsFactures.size,
        total:
          d.affaires.reduce((s, a) => s + (+a.total || 0), 0) +
          d.autres.reduce((s, f) => s + ttcDocument(f), 0),
        paye:
          d.affaires.reduce((s, a) => s + (+a.paye || 0), 0) +
          payeesAutres.reduce((s, f) => s + ttcDocument(f), 0),
      };
    })
    .sort((x, y) => nomClient(x.clientId).localeCompare(nomClient(y.clientId)));

  return (
    <Card style={{ marginBottom: 6, overflow: 'hidden' }}>
      <details>
        <summary
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            cursor: 'pointer',
            listStyle: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: C.green + '18',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}
          >
            🧾
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{tr('Factures clients')}</div>
            <div style={{ fontSize: 8.5, color: C.muted }}>
              {trf('{0} facture(s) · un dossier par client', nb)}
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: nb ? C.green : C.muted }}>
            {nb ? tr('▸ voir') : '—'}
          </div>
        </summary>
        <div style={{ padding: '0 14px 8px' }}>
          {dossiers.map((d) => (
            <Dossier
              key={String(d.clientId)}
              titre={'👤 ' + (nomClient(d.clientId) || tr('Client'))}
              sousTitre={trf('{0} chantier(s) · {1} facture(s)', d.affaires.length, d.nbFactures)}
              droite={trf('encaissé {0} / {1}', fmt(d.paye), fmt(d.total))}
              aLOuverture={() => (
                // Tout le détail du client : la marchandise achetée, la
                // sous-traitance et les photos jointes à ses offres et factures.
                <div style={{ marginTop: 8 }}>
                  <ListeDuClient devis={d.devis} factures={d.factures} chantiers={chantiers} />
                </div>
              )}
            >
              {d.affaires.map((a) => {
                const pieces = [...a.acomptes, ...a.enPreparation.filter((f) => f.acompte)];
                const finales = [...a.finales, ...a.enPreparation.filter((f) => !f.acompte)];
                return (
                  <div key={a.id} style={{ marginBottom: 10 }}>
                    <div style={sousTitre}>{'🏗️ ' + (a.titre || tr('Chantier'))}</div>
                    {/* Ce qui reste à encaisser : le reste à facturer, calculé
                        comme la facture finale, plus ce qui est facturé et pas payé. */}
                    <Tableau total={a.total} paye={a.paye} reste={a.reste + a.facture - a.paye}>
                      <Ligne
                        etape={tr('Offre')}
                        num={a.offreNum}
                        montant={a.offreTtc}
                        texte={
                          a.accepteeLe ? trf('✅ acceptée le {0}', a.accepteeLe) : tr('✅ acceptée')
                        }
                        couleur={C.green}
                        chemin={typeof a.id === 'number' ? `/devis/${a.id}/document` : null}
                      />
                      {a.avenants.map((av, i) => (
                        <Ligne
                          key={'av' + (av.id ?? i)}
                          etape={tr('Avenant')}
                          num={String(i + 1)}
                          montant={av.ttc}
                          texte={av.motif || av.date}
                        />
                      ))}
                      {pieces.map((f, i) => {
                        const [texte, couleur] = etat(f);
                        return (
                          <Ligne
                            key={f.id}
                            etape={i === 0 ? tr('Acompte') : tr('Avancement')}
                            num={f.num}
                            montant={f.ttc}
                            texte={texte}
                            couleur={couleur}
                            chemin={chemin(f)}
                            terminer={terminer(f)}
                          />
                        );
                      })}
                      {finales.map((f) => {
                        const [texte, couleur] = etat(f);
                        return (
                          <Ligne
                            key={f.id}
                            etape={tr('Finale')}
                            num={f.num}
                            montant={f.ttc}
                            texte={texte}
                            couleur={couleur}
                            chemin={chemin(f)}
                            terminer={terminer(f)}
                          />
                        );
                      })}
                      {finales.length === 0 && (
                        <Ligne
                          etape={tr('Finale')}
                          montant={a.reste}
                          texte={a.termine ? tr('⚠️ à faire') : tr('à la fin des travaux')}
                          couleur={a.termine ? C.amber : C.grey}
                        />
                      )}
                    </Tableau>
                  </div>
                );
              })}
              {d.autres.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={sousTitre}>{tr('📄 Autres factures (sans offre)')}</div>
                  <Tableau>
                    {d.autres.map((f) => {
                      const [texte, couleur] = etat({ ...f, payeeLe: f.paidDate });
                      return (
                        <Ligne
                          key={f.id}
                          etape={tr(etapeDeLaFacture(f, toutes))}
                          num={f.num || f.id}
                          montant={ttcDocument(f)}
                          texte={texte}
                          couleur={couleur}
                          chemin={chemin(f)}
                          terminer={terminer(f)}
                        />
                      );
                    })}
                  </Tableau>
                </div>
              )}
            </Dossier>
          ))}
          {nb === 0 && (
            <div style={{ fontSize: 9.5, color: C.muted, padding: 6 }}>
              {tr('Aucun document pour l’instant.')}
            </div>
          )}
        </div>
      </details>
    </Card>
  );
}
